import React from 'react';

import { Diff, Optional } from 'utility-types';

import { Context, GoogleAnalyticsConsumer } from './context';
import { isClassComponent } from './utils';

export type InjectedProps = Context;

type TrackMethod = (...args: any[]) => void;
type TrackMethodsConfig = Record<string, TrackMethod>;

interface GoogleAnalyticsConfig {
    trackMethods?: (props: any) => TrackMethodsConfig;
}

function createWrappedHandlers(
    trackedMethods: TrackMethodsConfig,
    props: Record<string, any>
): Record<string, TrackMethod> {
    return Object.entries(trackedMethods).reduce(
        (acc, [methodName, gaMethod]) => {
            acc[methodName] = (...args: any[]) => {
                // Call the GA tracking method
                gaMethod?.(...args);
                // Call the original handler if it exists in props
                const originalHandler = props[methodName];
                if (typeof originalHandler === 'function') {
                    return originalHandler(...args);
                }
            };
            return acc;
        },
        {} as Record<string, TrackMethod>
    );
}

function createFunctionalWrapper<BaseProps extends {}>(
    BaseComponent: React.FC<BaseProps>,
    config: GoogleAnalyticsConfig
) {
    const WrappedFunctionalComponent: React.FC<BaseProps & InjectedProps> = (props) => {
        const trackedMethods = config.trackMethods?.(props) || {};
        const wrappedHandlers = createWrappedHandlers(trackedMethods, props);

        return <BaseComponent {...props} {...wrappedHandlers} />;
    };

    // Set display name for debugging
    WrappedFunctionalComponent.displayName = `withGoogleAnalytics(${
        BaseComponent.displayName || BaseComponent.name || 'Component'
    })`;

    return WrappedFunctionalComponent;
}

function createClassWrapper<BaseProps extends {}>(
    BaseComponent: React.ComponentClass<BaseProps>,
    config: GoogleAnalyticsConfig
) {
    return class ExtendedBaseComponent extends BaseComponent {
        constructor(props: BaseProps & InjectedProps) {
            super(props);
            this.attachTrackedMethods(props);
        }

        private attachTrackedMethods(props: BaseProps & InjectedProps): void {
            const { trackMethods } = config;
            if (!trackMethods) return;

            const trackedMethods = trackMethods(props);
            Object.entries(trackedMethods).forEach(([methodName, gaMethod]) => {
                const originalMethod = this[methodName];
                this[methodName] = (...args: any[]) => {
                    gaMethod?.(...args);
                    return originalMethod?.call(this, ...args);
                };
            });
        }
    };
}

function withGAConsumer<BaseProps extends {}, NewProps extends Optional<InjectedProps>>(
    WrappedComponent: React.ComponentType<BaseProps & InjectedProps>
) {
    return (props: Diff<BaseProps, InjectedProps> & NewProps) => (
        <GoogleAnalyticsConsumer>
            {(ga) => (
                <WrappedComponent
                    trackSceneView={ga.trackSceneView}
                    trackEvent={ga.trackEvent}
                    trackCustom={ga.trackCustom}
                    {...props}
                />
            )}
        </GoogleAnalyticsConsumer>
    );
}

export default function withGoogleAnalytics<TInner, TOutter>(config: GoogleAnalyticsConfig = {}) {
    return function <BaseProps extends {}>(
        BaseComponent: React.ComponentClass<BaseProps> | React.FC<BaseProps>
    ) {
        if (!BaseComponent) {
            throw new Error('Component not passed to withGoogleAnalytics');
        }

        type NewProps = Optional<InjectedProps>;

        // Handle functional components with tracking
        if (config?.trackMethods && !isClassComponent(BaseComponent)) {
            const WrappedComponent = createFunctionalWrapper(BaseComponent, config);
            return withGAConsumer<BaseProps, NewProps>(WrappedComponent);
        }

        // Handle class components with tracking
        if (isClassComponent(BaseComponent)) {
            const WrappedComponent = createClassWrapper(BaseComponent, config);
            return withGAConsumer<BaseProps, NewProps>(WrappedComponent);
        }

        // Handle components without tracking (just inject GA props)
        return withGAConsumer<BaseProps, NewProps>(BaseComponent);
    };
}
